import React, { useEffect, useState, useRef } from 'react';
import {
  Stack,
  Paper,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { toast } from 'react-toastify';
import useUser from '../../hooks/useUser';
import useBranch from '../../hooks/useBranch';
import { useAuth } from '../../context/authContext';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const RESTRICTED_EMPLOYEE_DEPARTMENTS = [
  'München',
  'Augsburg',
  'Hamburg',
  'Denzlingen',
  'Kiel',
  'Mainz',
  'Frankfurt',
  'Düsseldorf',
  'Duisburg',
  'Essen-Kray',
  'Essen-Borbeck',
  'Mönchengladbach',
  'Bielefeld',
  'Köln',
];

const RESTRICTED_EMPLOYEE_JOB_FIELDS = [
  'KundenbetreuerIn',
  'Standortleitung',
  'stellv. Standortleitung',
  'Schichtleitung',
  'Bereichsleitung AZ',
  'Bereichsleitung GZ',
  'Bereichsleitung EZ',
  'Bereichsleitung LZ',
  'Bereichsleitung BZ',
];

export default function CardQueue() {
  const { isLoading, error, getAllNewUsers, startPrintProcess, migrateUsersFromCSV, updateUser } =
    useUser();
  const { getEmployeeCardStock, reportEmployeeCardMisprint } = useBranch();
  const { refreshCountForMenuItem, checkRight } = useAuth();
  const hasUserUpdateRight = checkRight('user:update');
  const hasPrintAssignedBranchesRight = checkRight('user:special:printAssignedBranches');
  const canEditCardQueueData = hasUserUpdateRight || hasPrintAssignedBranchesRight;
  const usesRestrictedCardQueueEdit = !hasUserUpdateRight && hasPrintAssignedBranchesRight;
  const canUseCsvUpload = !hasPrintAssignedBranchesRight;
  const canImportCardQueueUsers =
    canUseCsvUpload && checkRight('user:special:import') && checkRight('user:special:print');
  const [users, setUsers] = useState([]);
  const [employeeCardStock, setEmployeeCardStock] = useState([]);
  const [printing, setPrinting] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [csvFormat, setCsvFormat] = useState('withEmployeeId');
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState({});
  const [fieldValues, setFieldValues] = useState({});
  const [printDialogUser, setPrintDialogUser] = useState(null);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [misprintBranch, setMisprintBranch] = useState(null);
  const [misprintSaving, setMisprintSaving] = useState(false);
  const fieldRefs = useRef({});
  const fileInputRef = useRef(null);

  const decodeCsvFile = async file => {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);

    const hasUtf8BOM =
      bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf;
    const hasUtf16LEBOM = bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe;
    const hasUtf16BEBOM = bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff;

    if (hasUtf8BOM) {
      return new TextDecoder('utf-8').decode(bytes.subarray(3));
    }
    if (hasUtf16LEBOM) {
      return new TextDecoder('utf-16le').decode(bytes.subarray(2));
    }
    if (hasUtf16BEBOM) {
      return new TextDecoder('utf-16be').decode(bytes.subarray(2));
    }

    const tryDecode = (label, view) => {
      try {
        return new TextDecoder(label, { fatal: false }).decode(view);
      } catch (e) {
        return null;
      }
    };

    const candidates = ['utf-8', 'windows-1252', 'iso-8859-1', 'latin1', 'utf-16le', 'utf-16be'];
    for (const enc of candidates) {
      const text = tryDecode(enc, bytes);
      if (!text) continue;
      const bad = (text.match(/\uFFFD/g) || []).length;
      if (bad === 0) return text;
    }

    return new TextDecoder('windows-1252').decode(bytes);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const data = await getAllNewUsers();
      setUsers(Array.isArray(data) ? data : []);
    };
    fetchUsers();
    fetchEmployeeCardStock();
  }, []);

  const fetchEmployeeCardStock = async () => {
    try {
      const data = await getEmployeeCardStock();
      setEmployeeCardStock(Array.isArray(data?.branches) ? data.branches : []);
    } catch (err) {
      toast.error('Fehler beim Laden der Kartenbestände');
    }
  };

  const getUserBranchOptions = user =>
    (user?.assignedBranches || [])
      .map(branch => ({
        _id: branch.branchId?._id || branch.branchId,
        name: branch.branchId?.locationName || branch.branchId?.longName || branch.name || 'Standort',
      }))
      .filter(branch => !!branch._id);

  const getRequiredMissingFields = user =>
    [
      !user.firstName && 'Vorname',
      !user.lastName && 'Nachname',
      !user.employmentData?.department && 'Abteilung/Standort',
      !user.employmentData?.jobField && 'Berufsbezeichnung',
    ].filter(Boolean);

  const getStockForBranch = branchId =>
    employeeCardStock.find(stock => String(stock.branchId) === String(branchId));

  const handleOpenPrintDialog = user => {
    const branchOptions = getUserBranchOptions(user);
    setPrintDialogUser(user);
    setSelectedBranchId(branchOptions[0]?._id || '');
  };

  const handleClosePrintDialog = () => {
    setPrintDialogUser(null);
    setSelectedBranchId('');
  };

  const handlePrint = async (userId, branchId) => {
    setPrinting(prev => ({ ...prev, [userId]: true }));
    try {
      await startPrintProcess(userId, branchId);
      toast.success('Mitarbeiterkartendruck wurde gestartet');
      handleClosePrintDialog();
      const data = await getAllNewUsers();
      setUsers(Array.isArray(data) ? data : []);
      await fetchEmployeeCardStock();
      refreshCountForMenuItem('Mitarbeiterausweise');
    } catch (err) {
      toast.error('Fehler beim Starten des Mitarbeiterkartendrucks');
    } finally {
      setPrinting(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleReportMisprint = async event => {
    event.preventDefault();
    if (!misprintBranch) return;

    const formData = new FormData(event.currentTarget);
    const quantity = Number.parseInt(formData.get('quantity') || '1', 10);
    const note = String(formData.get('note') || '').trim();

    if (!Number.isInteger(quantity) || quantity <= 0) {
      toast.error('Bitte eine gültige Anzahl eingeben');
      return;
    }

    setMisprintSaving(true);
    try {
      await reportEmployeeCardMisprint(misprintBranch.branchId, { quantity, note });
      toast.success('Fehldruck wurde gemeldet');
      setMisprintBranch(null);
      await fetchEmployeeCardStock();
    } catch (err) {
      toast.error('Fehler beim Melden des Fehldrucks');
    } finally {
      setMisprintSaving(false);
    }
  };

  const handleFileUpload = async event => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setUploadResult({
        type: 'error',
        message: 'Bitte wähle eine CSV-Datei aus.',
      });
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const csvData = await decodeCsvFile(file);
      const result = await migrateUsersFromCSV(csvData, csvFormat);

      const resultType = result.successCount > 0 ? 'success' : result.warning ? 'warning' : 'error';

      setUploadResult({
        type: resultType,
        message: result.message,
        details: result,
      });

      // refresh list after upload (even if partially successful)
      if (result.successCount > 0) {
        const data = await getAllNewUsers();
        setUsers(Array.isArray(data) ? data : []);
        refreshCountForMenuItem('Mitarbeiterausweise');
      }
    } catch (error) {
      setUploadResult({
        type: 'error',
        message: error.message || 'Fehler beim Hochladen der CSV-Datei.',
      });
    } finally {
      setUploading(false);
      // reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleEditClick = userId => {
    const user = users.find(u => u._id === userId);
    if (user) {
      const department = user.employmentData?.department || '';
      const jobField = user.employmentData?.jobField || '';

      setEditing(prev => ({ ...prev, [userId]: true }));
      // Initialize field refs with current values
      fieldRefs.current[userId] = {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        department:
          usesRestrictedCardQueueEdit && !RESTRICTED_EMPLOYEE_DEPARTMENTS.includes(department)
            ? ''
            : department,
        jobField:
          usesRestrictedCardQueueEdit && !RESTRICTED_EMPLOYEE_JOB_FIELDS.includes(jobField)
            ? ''
            : jobField,
      };
    }
  };

  const handleCancelEdit = userId => {
    setEditing(prev => ({ ...prev, [userId]: false }));
    delete fieldRefs.current[userId];
  };

  const handleSaveEdit = async userId => {
    setSaving(prev => ({ ...prev, [userId]: true }));
    try {
      const values = fieldRefs.current[userId];
      if (
        usesRestrictedCardQueueEdit &&
        (!RESTRICTED_EMPLOYEE_DEPARTMENTS.includes(values.department) ||
          !RESTRICTED_EMPLOYEE_JOB_FIELDS.includes(values.jobField))
      ) {
        toast.error('Bitte Abteilung und Berufsbezeichnung aus der Liste auswählen');
        return;
      }

      const updateData = {
        firstName: values.firstName,
        lastName: values.lastName,
        employmentData: {
          department: values.department,
          jobField: values.jobField,
        },
      };

      await updateUser(userId, updateData);
      setEditing(prev => ({ ...prev, [userId]: false }));
      delete fieldRefs.current[userId];
      // Refresh the user list
      const data = await getAllNewUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  const renderEmploymentDataFields = userId => {
    if (usesRestrictedCardQueueEdit) {
      return (
        <>
          <FormControl fullWidth size='small'>
            <InputLabel id={`cardqueue-department-${String(userId)}`}>Abteilung/Standort</InputLabel>
            <Select
              labelId={`cardqueue-department-${String(userId)}`}
              defaultValue={fieldRefs.current[userId]?.department || ''}
              label='Abteilung/Standort'
              onChange={e => handleFieldChange(userId, 'department', e.target.value)}
            >
              <MenuItem value='' disabled>
                Abteilung/Standort auswählen
              </MenuItem>
              {RESTRICTED_EMPLOYEE_DEPARTMENTS.map(department => (
                <MenuItem key={department} value={department}>
                  {department}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small'>
            <InputLabel id={`cardqueue-jobfield-${String(userId)}`}>Berufsbezeichnung</InputLabel>
            <Select
              labelId={`cardqueue-jobfield-${String(userId)}`}
              defaultValue={fieldRefs.current[userId]?.jobField || ''}
              label='Berufsbezeichnung'
              onChange={e => handleFieldChange(userId, 'jobField', e.target.value)}
            >
              <MenuItem value='' disabled>
                Berufsbezeichnung auswählen
              </MenuItem>
              {RESTRICTED_EMPLOYEE_JOB_FIELDS.map(jobField => (
                <MenuItem key={jobField} value={jobField}>
                  {jobField}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      );
    }

    return (
      <>
        <TextField
          size='small'
          label='Abteilung/Standort'
          defaultValue={fieldRefs.current[userId]?.department || ''}
          onChange={e => handleFieldChange(userId, 'department', e.target.value)}
          fullWidth
        />
        <TextField
          size='small'
          label='Berufsbezeichnung'
          defaultValue={fieldRefs.current[userId]?.jobField || ''}
          onChange={e => handleFieldChange(userId, 'jobField', e.target.value)}
          fullWidth
        />
      </>
    );
  };

  const handleFieldChange = (userId, field, value) => {
    // Update the ref directly without triggering re-renders
    if (fieldRefs.current[userId]) {
      fieldRefs.current[userId][field] = value;
    }
  };

  if (isLoading && users.length === 0) {
    return (
      <Stack
        alignItems='center'
        justifyContent='center'
        sx={{ ...viewRootTextPrimarySx, width: '100%', marginTop: '50px' }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack
        alignItems='center'
        justifyContent='center'
        sx={{ ...viewRootTextPrimarySx, width: '100%', marginTop: '50px' }}
      >
        <Typography color='error'>Fehler: {error}</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ ...viewRootTextPrimarySx, width: '100%', marginTop: '40px', alignItems: 'center' }}>
      <Typography variant='h5' component='div' align='center' sx={{ mb: 2 }}>
        Mitarbeiter ohne Mitarbeiterausweis
      </Typography>

      {/* Card stock per branch. A uniform grid rather than wrapped pills: the branch
          names differ wildly in length, so free-flowing chips produced a ragged,
          hard-to-scan column. */}
      {employeeCardStock.length > 0 && (
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gap: 1,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          }}
        >
          {employeeCardStock.map(stock => (
            <Stack
              key={stock.branchId}
              direction='row'
              spacing={1}
              alignItems='center'
              justifyContent='space-between'
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant='body2' noWrap sx={{ fontWeight: 500 }}>
                  {stock.locationName || stock.longName}
                </Typography>
                <Typography
                  variant='caption'
                  sx={{
                    color: stock.isLowStock ? 'warning.main' : 'text.secondary',
                    fontWeight: stock.isLowStock ? 500 : 400,
                  }}
                >
                  {stock.remaining} Mitarbeiterkarten übrig
                </Typography>
              </Box>
              <Button
                size='small'
                color='warning'
                sx={{ flexShrink: 0 }}
                onClick={() => setMisprintBranch(stock)}
              >
                Fehldruck melden
              </Button>
            </Stack>
          ))}
        </Box>
      )}

      {users.length === 0 ? (
        <Typography variant='body1' align='center'>
          Keine neuen Benutzer gefunden.
        </Typography>
      ) : (
        users.map(user => (
          <Paper
            key={user._id}
            data-testid={`cardqueue-row-${String(user._id)}`}
            elevation={3}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderRadius: 2,
            }}
          >
            <Stack direction='row' alignItems='center' spacing={2} sx={{ flex: 1 }}>
              {user.firstName && user.lastName ? (
                <Stack spacing={1} sx={{ flex: 1 }}>
                  {editing[user._id] ? (
                    <Stack direction='row' spacing={1}>
                      <TextField
                        size='small'
                        label='Vorname'
                        defaultValue={fieldRefs.current[user._id]?.firstName || ''}
                        onChange={e => handleFieldChange(user._id, 'firstName', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size='small'
                        label='Nachname'
                        defaultValue={fieldRefs.current[user._id]?.lastName || ''}
                        onChange={e => handleFieldChange(user._id, 'lastName', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                    </Stack>
                  ) : (
                    <Typography variant='body1' sx={{ fontWeight: 500 }}>
                      {user.firstName} {user.lastName}
                    </Typography>
                  )}

                  {editing[user._id] ? (
                    <Stack spacing={1} sx={{ maxWidth: 400 }}>
                      {renderEmploymentDataFields(user._id)}
                    </Stack>
                  ) : !user.firstName ||
                    !user.lastName ||
                    !user.employmentData?.department ||
                    !user.employmentData?.jobField ? (
                    <Typography color='error' variant='caption' sx={{ fontSize: '0.75rem' }}>
                      Fehlende Daten: {getRequiredMissingFields(user).join(', ')}
                    </Typography>
                  ) : (
                    <Stack spacing={0.5}>
                      <Typography
                        variant='caption'
                        sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                      >
                        {user.employmentData?.department} • {user.employmentData?.jobField}
                      </Typography>
                      {user.cardStatus === 'print_failed' && (
                        <Typography color='error' variant='caption' sx={{ fontSize: '0.75rem' }}>
                          Letzter Druckversuch fehlgeschlagen. Bitte erneut versuchen.
                        </Typography>
                      )}
                      {user.cardStatus === 'link_failed' && (
                        <Typography
                          color='warning.main'
                          variant='caption'
                          sx={{ fontSize: '0.75rem' }}
                        >
                          Karte wurde gedruckt, aber der Chip konnte nicht gelesen werden. Bitte
                          Karte erneut drucken oder die Kartennummer manuell hinterlegen.
                        </Typography>
                      )}
                    </Stack>
                  )}
                </Stack>
              ) : (
                <Stack spacing={1} sx={{ flex: 1 }}>
                  {editing[user._id] ? (
                    <Stack direction='row' spacing={1}>
                      <TextField
                        size='small'
                        label='Vorname'
                        defaultValue={fieldRefs.current[user._id]?.firstName || ''}
                        onChange={e => handleFieldChange(user._id, 'firstName', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size='small'
                        label='Nachname'
                        defaultValue={fieldRefs.current[user._id]?.lastName || ''}
                        onChange={e => handleFieldChange(user._id, 'lastName', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                    </Stack>
                  ) : (
                    <Typography color='error' sx={{ fontWeight: 500 }}>
                      Vorname und Nachname fehlen für{' '}
                      {user.email ? user.email : user.username ? user.username : 'diesen Benutzer'}
                    </Typography>
                  )}

                  {editing[user._id] ? (
                    <Stack spacing={1} sx={{ maxWidth: 400 }}>
                      {renderEmploymentDataFields(user._id)}
                    </Stack>
                  ) : (
                    <Typography color='error' variant='caption' sx={{ fontSize: '0.75rem' }}>
                      Fehlende Daten: {getRequiredMissingFields(user).join(', ')}
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>

            <Stack direction='row' spacing={1} alignItems='center'>
              {canEditCardQueueData &&
                (editing[user._id] ? (
                  <>
                    <Tooltip title='Speichern'>
                      <IconButton
                        data-testid={`cardqueue-save-${String(user._id)}`}
                        color='success'
                        onClick={() => handleSaveEdit(user._id)}
                        disabled={!!saving[user._id]}
                      >
                        {saving[user._id] ? <CircularProgress size={20} /> : <SaveIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Abbrechen'>
                      <IconButton
                        data-testid={`cardqueue-cancel-${String(user._id)}`}
                        color='error'
                        onClick={() => handleCancelEdit(user._id)}
                        disabled={!!saving[user._id]}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip title='Daten bearbeiten'>
                    <IconButton
                      data-testid={`cardqueue-edit-${String(user._id)}`}
                      color='primary'
                      onClick={() => handleEditClick(user._id)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                ))}

              {getRequiredMissingFields(user).length === 0 && (
                <Button
                  data-testid={`cardqueue-confirm-print-${String(user._id)}`}
                  variant='contained'
                  color='success'
                  startIcon={<PrintIcon />}
                  onClick={() => handleOpenPrintDialog(user)}
                  disabled={!!printing[user._id] || !!editing[user._id]}
                >
                  {printing[user._id] ? <CircularProgress size={24} color='inherit' /> : 'Drucken'}
                </Button>
              )}
            </Stack>
          </Paper>
        ))
      )}

      {canImportCardQueueUsers && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', pt: 4 }}>
            <FormControl sx={{ minWidth: 300, maxWidth: '90%' }}>
              <InputLabel id='csv-format-label'>CSV-Format</InputLabel>
              <Select
                labelId='csv-format-label'
                value={csvFormat}
                label='CSV-Format'
                onChange={e => setCsvFormat(e.target.value)}
              >
                <MenuItem value='withEmployeeId'>Nachname,Vorname,Mitarbeiternummer,Standort</MenuItem>
                <MenuItem value='withoutEmployeeId'>Nachname,Vorname,Standort</MenuItem>
              </Select>
            </FormControl>

            <Button
              data-testid='cardqueue-upload-button'
              variant='contained'
              color='primary'
              startIcon={
                uploading ? <CircularProgress size={20} color='inherit' /> : <UploadFileIcon />
              }
              onClick={handleUploadClick}
              disabled={uploading}
            >
              {uploading ? 'Wird hochgeladen...' : 'CSV hochladen'}
            </Button>
          </Box>

          <input
            type='file'
            ref={fileInputRef}
            data-testid='cardqueue-upload-input'
            onChange={handleFileUpload}
            accept='.csv,text/csv'
            style={{ display: 'none' }}
          />

          {uploadResult && (
            <Alert
              severity={uploadResult.type}
              sx={{ width: '100%', maxWidth: '600px' }}
              onClose={() => setUploadResult(null)}
            >
              <Typography variant='body2'>{uploadResult.message}</Typography>
              {uploadResult.details &&
                (uploadResult.type === 'success' || uploadResult.type === 'warning') && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant='caption' display='block'>
                      Erfolgreich erstellt: {uploadResult.details.successCount}
                    </Typography>
                    <Typography variant='caption' display='block'>
                      Fehlgeschlagen: {uploadResult.details.failureCount}
                    </Typography>
                    {uploadResult.details.warning && (
                      <Typography variant='caption' display='block' color='warning.main'>
                        Warnung: {uploadResult.details.warning}
                      </Typography>
                    )}
                  </Box>
                )}
            </Alert>
          )}
        </>
      )}

      <Dialog open={!!printDialogUser} onClose={handleClosePrintDialog} maxWidth='sm' fullWidth>
        <DialogTitle>Mitarbeiterkarte drucken</DialogTitle>
        <DialogContent>
          {printDialogUser && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity='warning'>
                Jetzt müssen Mitarbeiterkarten eingelegt sein, nicht die normalen Spielerkarten. Der
                Druck läuft über denselben Kartendrucker am ausgewählten Standort.
              </Alert>
              <Alert severity='info'>
                Die Karte wird in Copago erst am Folgetag aktiv, weil der Syncserver nur alle 24
                Stunden läuft.
              </Alert>
              <Typography variant='body2'>
                Karte für {printDialogUser.firstName} {printDialogUser.lastName} drucken.
              </Typography>
              {getUserBranchOptions(printDialogUser).length > 1 ? (
                <FormControl fullWidth size='small'>
                  <InputLabel id='employee-card-branch-label'>Standort</InputLabel>
                  <Select
                    labelId='employee-card-branch-label'
                    value={selectedBranchId}
                    label='Standort'
                    onChange={e => setSelectedBranchId(e.target.value)}
                  >
                    {getUserBranchOptions(printDialogUser).map(branch => (
                      <MenuItem key={branch._id} value={branch._id}>
                        {branch.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : getUserBranchOptions(printDialogUser).length === 1 ? (
                <Typography variant='body2' color='text.secondary'>
                  Standort: {getUserBranchOptions(printDialogUser)[0].name}
                </Typography>
              ) : (
                <Alert severity='error'>Für diesen Mitarbeiter ist kein Standort hinterlegt.</Alert>
              )}
              <Alert severity='info'>
                Ablauf: Mitarbeiterkarten einlegen, Druck starten, Karte im Drucker lassen, bis RFID
                gelesen und der Druck abgeschlossen ist. Danach wird die Karte automatisch am
                Mitarbeiter hinterlegt.
              </Alert>
              {selectedBranchId && getStockForBranch(selectedBranchId) && (
                <Alert severity={getStockForBranch(selectedBranchId).isLowStock ? 'warning' : 'info'}>
                  Erwarteter Restbestand am Standort:{' '}
                  <b>{getStockForBranch(selectedBranchId).remaining}</b> Mitarbeiterkarten-Rohlinge.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePrintDialog} disabled={!!printing[printDialogUser?._id]}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='success'
            onClick={() => handlePrint(printDialogUser._id, selectedBranchId)}
            disabled={
              !printDialogUser ||
              !selectedBranchId ||
              !!printing[printDialogUser?._id] ||
              getUserBranchOptions(printDialogUser).length === 0
            }
            startIcon={printing[printDialogUser?._id] ? <CircularProgress size={16} /> : <PrintIcon />}
          >
            Druck starten
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!misprintBranch} onClose={() => setMisprintBranch(null)} maxWidth='sm' fullWidth>
        <Box component='form' onSubmit={handleReportMisprint}>
          <DialogTitle>Fehldruck melden</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity='warning'>
                Fehldrucke müssen aufbewahrt werden. Bitte die Karte nicht wegwerfen, sondern
                gesammelt zurückhalten.
              </Alert>
              <Typography variant='body2'>
                Standort: {misprintBranch?.locationName || misprintBranch?.longName}
              </Typography>
              <TextField
                name='quantity'
                label='Anzahl Fehldrucke'
                type='number'
                defaultValue={1}
                inputProps={{ min: 1, step: 1 }}
                fullWidth
                required
              />
              <TextField name='note' label='Notiz' multiline minRows={2} fullWidth />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMisprintBranch(null)} disabled={misprintSaving}>
              Abbrechen
            </Button>
            <Button type='submit' variant='contained' color='warning' disabled={misprintSaving}>
              {misprintSaving ? <CircularProgress size={18} /> : 'Fehldruck melden'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Stack>
  );
}
